import React, { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress, TextField } from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import { useDispatch } from 'react-redux';
import { handleLogout } from '../../data/coreActions';
import { get, post } from '../../utils/ajax';
import { remoteRoutes } from '../../data/constants';
import Toast from '../../utils/Toast';
import elevateLogo from '../../assets/images/elevate-logo.png';
import { fontFamilies } from '../../theme/fonts';
import { colors } from '../../theme/tokens';

const CORAL = colors.brand;
const ORANGE = colors.accentOrange;

interface IStatus {
  status: string;
  hubName: string | null;
  email: string;
  emailVerified: boolean;
  rejectionReason: string | null;
}

/**
 * Where a self-registered student lands until a hub manager or admin lets
 * them in. Showing this beats refusing the login outright — they can see the
 * hub they picked, confirm their email while they wait, and tell whether
 * anything is actually wrong.
 */
const PendingApproval = () => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState<IStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const load = useCallback((quiet?: boolean) => {
    if (!quiet) setLoading(true);
    get(
      remoteRoutes.registrationStatus,
      (resp) => {
        // Once they're approved, reload rather than just re-render: the
        // user held in redux (and localStorage) still says PENDING, and a
        // cold boot re-reads it from /auth/me, which reports the live
        // status from the database.
        if (resp?.status === 'APPROVED') {
          Toast.success('You have been approved — welcome!');
          window.setTimeout(() => window.location.reload(), 900);
          return;
        }
        setStatus(resp);
      },
      () => undefined,
      () => setLoading(false),
    );
  }, []);

  useEffect(() => {
    load();
    // Approval happens elsewhere, so poll quietly — someone sitting on this
    // page gets let in without having to know to refresh.
    const timer = setInterval(() => load(true), 30000);
    return () => clearInterval(timer);
  }, [load]);

  const sendCode = () => {
    setSending(true);
    post(
      remoteRoutes.emailVerificationSend,
      {},
      () => {
        setCodeSent(true);
        Toast.info('We have sent a code to your email address');
      },
      (err: any) =>
        Toast.error(
          err?.response?.body?.message || 'Could not send the code just now',
        ),
      () => setSending(false),
    );
  };

  const confirmCode = () => {
    setConfirming(true);
    post(
      remoteRoutes.emailVerificationConfirm,
      { code },
      () => {
        Toast.success('Email confirmed');
        setCode('');
        load(true);
      },
      (err: any) =>
        Toast.error(err?.response?.body?.message || "That code isn't right"),
      () => setConfirming(false),
    );
  };

  const rejected = status?.status === 'REJECTED';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.bgSubtle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <CssBaseline />
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: colors.bgSurface,
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(16,24,40,0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: rejected
              ? colors.textMuted
              : `linear-gradient(120deg, ${CORAL} 0%, ${ORANGE} 100%)`,
            color: colors.onBrand,
            padding: '26px 28px',
          }}
        >
          <img
            src={elevateLogo}
            alt="era92 elevate"
            style={{
              height: 34,
              marginBottom: 12,
              filter: 'brightness(0) invert(1)',
            }}
          />
          <div
            style={{
              fontFamily: fontFamilies.display,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {rejected
              ? 'Your registration was not approved'
              : 'Thanks for registering!'}
          </div>
          <div style={{ fontSize: 14, opacity: 0.93, marginTop: 6 }}>
            {rejected
              ? 'Please get in touch with your hub if you think this is a mistake.'
              : 'Your details are with the hub for review. You will get access as soon as they approve you.'}
          </div>
        </div>

        <div style={{ padding: '24px 28px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <CircularProgress size={26} />
            </div>
          ) : (
            <>
              <Row label="Email" value={status?.email ?? '—'} />
              <Row label="Hub" value={status?.hubName ?? 'Not set'} />
              <Row
                label="Status"
                value={rejected ? 'Not approved' : 'Waiting for approval'}
              />
              {rejected && status?.rejectionReason && (
                <Row label="Reason" value={status.rejectionReason} />
              )}

              {!rejected && (
                <div
                  style={{
                    marginTop: 18,
                    padding: 16,
                    borderRadius: 12,
                    background: status?.emailVerified
                      ? colors.tintGreen
                      : colors.tintOrange,
                    border: `1px solid ${
                      status?.emailVerified
                        ? colors.tintGreen
                        : colors.tintOrange
                    }`,
                  }}
                >
                  {status?.emailVerified ? (
                    <div style={{ fontSize: 14, color: colors.onGreen }}>
                      ✓ Your email address is confirmed.
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: 14,
                          color: colors.onOrange,
                          marginBottom: 10,
                        }}
                      >
                        Confirm your email address so the hub knows how to reach
                        you.
                      </div>
                      {codeSent ? (
                        <div style={{ display: 'flex', gap: 10 }}>
                          <TextField
                            label="6-digit code"
                            variant="outlined"
                            size="small"
                            value={code}
                            inputProps={{ inputMode: 'numeric' }}
                            onChange={(e) =>
                              setCode(
                                String(e.target.value)
                                  .replace(/\D/g, '')
                                  .slice(0, 6),
                              )
                            }
                          />
                          <Button
                            variant="contained"
                            disableElevation
                            disabled={confirming || code.length < 6}
                            onClick={confirmCode}
                            style={{
                              background: CORAL,
                              color: colors.onBrand,
                              textTransform: 'none',
                              borderRadius: 8,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {confirming ? 'Checking…' : 'Confirm'}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outlined"
                          disabled={sending}
                          onClick={sendCode}
                          style={{ textTransform: 'none', borderRadius: 8 }}
                        >
                          {sending ? 'Sending…' : 'Send me a code'}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}

              <div
                style={{
                  marginTop: 22,
                  display: 'flex',
                  gap: 12,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button
                  onClick={() => load()}
                  style={{ textTransform: 'none' }}
                >
                  Check again
                </Button>
                <Button
                  onClick={() => dispatch(handleLogout())}
                  style={{
                    textTransform: 'none',
                    color: colors.textMuted,
                  }}
                >
                  Sign out
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 16,
      padding: '9px 0',
      borderBottom: `1px solid ${colors.border}`,
      fontSize: 14,
    }}
  >
    <span style={{ color: colors.textMuted }}>{label}</span>
    <span
      style={{
        color: colors.textPrimary,
        fontWeight: 600,
        textAlign: 'right',
      }}
    >
      {value}
    </span>
  </div>
);

export default PendingApproval;
